// Body of the toppers ContactFormPage: the UtilityPage layout (side nav +
// ContentTitle + right bar) with the rich-text intro and the contact form in
// content__copy. The form is rendered natively in React from the
// reactivated-serialized ContactFormRenderable; the captcha is the standalone
// <ReactivatedReCaptchaField> (excluded from serialization).
import { Immutable } from "immer";
import React from "react";
import { ReactivatedFormWidget } from "tsi-common-react/src/forms/ReactivatedFormWidget/index";
import {
    ReactivatedReCaptchaField,
    useReCaptchaSubmit,
} from "tsi-common-react/src/forms/ReactivatedReCaptcha/index";
import {
    FORM_CLASS_NAME,
    FORM_ERROR_CLASS_NAME,
    FORM_ERROR_LIST_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";
import { useFocusFirstFormError } from "tsi-common-react/src/forms/hooks/useFocusFirstFormError";
import { t } from "ttag";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { RightBarSection } from "../../blocks/RightBarSection";
import { ContentTitle } from "../ContentTitle";
import { UtilitySideNav } from "../UtilitySideNav";

type IProps = Immutable<templates.ContactFormPageTemplate>;

const ContactForm = (
    props: Pick<IProps, "form" | "recaptcha_action">,
): React.JSX.Element => {
    const { form } = props;
    const onSubmit = useReCaptchaSubmit(props.recaptcha_action);
    // The "recipients" field (id="reasons") repurposes the message field for
    // one reason ("Unsubscribe from Postal" -> mailing address instead of a
    // free-text message); swap its label to match the selection.
    const [messageLabel, setMessageLabel] = React.useState<string | null>(null);
    const handleReasonChange: React.ChangeEventHandler<HTMLSelectElement> = (
        event,
    ) => {
        const selected = event.target.options[event.target.selectedIndex].text;
        // TODO(i18n): Wrap hardcoded strings in text comparison and
        // assignment with ttag t: selected === t`Unsubscribe from Postal` ?
        // t`Mailing Address:` : t`Your Message:`
        setMessageLabel(
            selected === "Unsubscribe from Postal"
                ? "Mailing Address:"
                : "Your Message:",
        );
    };
    useFocusFirstFormError(form);
    return (
        <>
            {form.errors && (
                <div className="contact-form__errors-title">
                    {t`There was an error submitting the form. Scroll down for details.`}
                </div>
            )}
            <form
                action=""
                method="post"
                className={concatClassNames([
                    FORM_CLASS_NAME,
                    "form--contact",
                    "js-kuid-form",
                ])}
                noValidate
                onSubmit={onSubmit}
            >
                <CSRFToken />
                {form.iterator.map((name) => {
                    const field = form.fields[name];
                    const errors = form.errors?.[name];
                    const label =
                        name === "message" && messageLabel !== null
                            ? messageLabel
                            : field.label;
                    return (
                        <div className={FORM_FIELD_CLASS_NAME} key={name}>
                            <ReactivatedFormWidget
                                widget={field.widget}
                                onChange={
                                    name === "recipients"
                                        ? handleReasonChange
                                        : undefined
                                }
                            />
                            <label htmlFor={field.widget.attrs.id}>
                                {label}
                            </label>
                            {errors && errors.length > 0 && (
                                <ul
                                    className={FORM_ERROR_LIST_CLASS_NAME}
                                    id={`${field.widget.name}-errors`}
                                >
                                    {errors.map((error, i) => (
                                        <li
                                            className={FORM_ERROR_CLASS_NAME}
                                            key={i}
                                        >
                                            {error}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })}
                <ReactivatedReCaptchaField />
                <input
                    type="submit"
                    value={t`Submit`}
                    className="button button--full-width"
                />
            </form>
        </>
    );
};

export const ContactFormPageBody = (props: IProps): React.JSX.Element => {
    const {
        nav_items,
        has_right_bar,
        right_bar_items,
        content_title,
        content_title_phone_href,
        content_title_phone_display,
        intro_html,
    } = props;
    return (
        <div className="l-capped-width u-margin-bottom--xl u-full-width-title">
            {nav_items.length > 0 && (
                <div className="l-side-nav">
                    <UtilitySideNav items={nav_items} />
                </div>
            )}

            <div className="l-side-nav-content">
                <ContentTitle
                    title={content_title}
                    phoneHref={content_title_phone_href}
                    phoneDisplay={content_title_phone_display}
                />

                <div
                    className={
                        has_right_bar
                            ? "content__copy"
                            : "content__copy--full-width"
                    }
                >
                    <div
                        className="rich-text"
                        dangerouslySetInnerHTML={{ __html: intro_html }}
                    />
                    <ContactForm
                        form={props.form}
                        recaptcha_action={props.recaptcha_action}
                    />
                </div>

                {has_right_bar && (
                    <aside className="content__aside">
                        {right_bar_items.map((item, i) =>
                            item.block_type === "right_bar_section" &&
                            item.section ? (
                                <div
                                    key={i}
                                    className={`w-block-${item.block_type} block-${item.block_type}`}
                                >
                                    <RightBarSection value={item.section} />
                                </div>
                            ) : (
                                <div
                                    key={i}
                                    className={`w-block-${item.block_type} block-${item.block_type}`}
                                    dangerouslySetInnerHTML={{
                                        __html: item.html,
                                    }}
                                />
                            ),
                        )}
                    </aside>
                )}
            </div>
        </div>
    );
};
